import { PageTransitionAnimation } from '../PageTransition';
import { runtime } from '../Runtime';
import { createPreloaderLoops, killPreloaderLoops, updatePreloaderProgress } from './configureLoader';

const duration = (value, fallback) => Number.isFinite(Number(value))
    ? Math.max(0, Number(value))
    : fallback;
const pageTargets = ({ viewport, header }) => [viewport, header].filter(Boolean);

export const configurePageTransition = () => {
    let activePreloader = null;

    const disposePreloader = () => {
        if(!activePreloader){
            return;
        }

        const { gsap, loader, loops, stopProgress } = activePreloader;
        stopProgress();
        killPreloaderLoops({ gsap, loader, loops });
        loader.querySelectorAll('svg').forEach(svg => svg.pauseAnimations?.());
        loader.classList.add('is-complete');
        gsap.set(loader, { autoAlpha: 0, pointerEvents: 'none', display: 'none' });
        activePreloader = null;
    };

    const startPreloader = ({ gsap, loader, reducedMotion }) => {
        disposePreloader();
        const config = runtime.system?.zeroUn?.preloader || {};

        if(!loader || config.enabled === false){
            return null;
        }

        const motion = config.motion || {};
        const startedAt = performance.now();
        const progress = { value: 0 };
        const isSwipePanel = loader.classList.contains('zero-un-preloader--swipe-panel');
        const smoothing = Math.max(1, Math.min(30, duration(motion.progressSmoothing, 8))) / 100;
        let frame = 0;
        let stopped = false;

        loader.classList.remove('is-complete');
        updatePreloaderProgress(loader, 0);
        gsap.set(loader, { display: 'grid', autoAlpha: 0, pointerEvents: 'auto' });
        loader.querySelectorAll('svg').forEach(svg => {
            svg.setCurrentTime?.(0);
            if(!reducedMotion) svg.unpauseAnimations?.();
        });

        const tick = () => {
            if(stopped){
                return;
            }

            // Loading stays below 100% until the incoming page is ready to enter.
            const elapsed = (performance.now() - startedAt) / 1000;
            const target = Math.min(92, 22 + elapsed * 28);
            progress.value += (target - progress.value) * smoothing;
            updatePreloaderProgress(loader, progress.value);
            frame = window.requestAnimationFrame(tick);
        };

        if(!reducedMotion && !isSwipePanel){
            frame = window.requestAnimationFrame(tick);
        }

        activePreloader = {
            gsap, loader, motion, startedAt, progress, isSwipePanel,
            loops: reducedMotion ? [] : createPreloaderLoops({ gsap, loader }),
            stopProgress: () => {
                stopped = true;
                window.cancelAnimationFrame(frame);
            }
        };

        return activePreloader;
    };

    PageTransitionAnimation
        .setLeave((context) => {
            const { gsap } = context;
            const preloader = startPreloader(context);
            const animation = gsap.timeline();

            animation.to(pageTargets(context), {
                autoAlpha: 0, pointerEvents: 'none', duration: 0.2, ease: 'power2.out'
            }, 0);

            if(preloader){
                animation.to(preloader.loader, { autoAlpha: 1, duration: 0.2 }, 0);
            }

            return animation;
        }, (context) => {
            const preloader = startPreloader(context);
            context.gsap.set(pageTargets(context), { autoAlpha: 0, pointerEvents: 'none' });
            if(preloader) context.gsap.set(preloader.loader, { autoAlpha: 1 });
        })
        .setEnter((context) => {
            const { gsap } = context;
            const preloader = activePreloader;
            const animation = gsap.timeline({
                onComplete: () => {
                    if(activePreloader === preloader) disposePreloader();
                }
            });
            let revealAt = 0;

            if(preloader){
                const { loader, motion, progress, startedAt, isSwipePanel } = preloader;
                preloader.stopProgress();
                const elapsed = (performance.now() - startedAt) / 1000;
                const remaining = Math.max(0, duration(motion.minimumDuration, 0.8) - elapsed);
                const progressDuration = isSwipePanel ? duration(motion.panelRevealDuration, 0.45) : 0.25;
                revealAt = remaining + progressDuration;

                animation.to(progress, {
                    value: 100,
                    duration: progressDuration,
                    ease: 'power2.out',
                    onUpdate: () => updatePreloaderProgress(loader, progress.value)
                }, remaining);
                animation.call(() => {
                    updatePreloaderProgress(loader, 100);
                    loader.classList.add('is-complete');
                }, [], revealAt);
                animation.to(loader, {
                    autoAlpha: 0,
                    duration: duration(motion.exitDuration, 0.65),
                    ease: 'power2.inOut'
                }, revealAt);
            }

            animation.to(pageTargets(context), {
                autoAlpha: 1, pointerEvents: 'initial', duration: 0.2, ease: 'power2.out'
            }, revealAt);

            return animation;
        }, (context) => {
            if(activePreloader) updatePreloaderProgress(activePreloader.loader, 100);
            disposePreloader();
            context.gsap.set(pageTargets(context), { autoAlpha: 1, pointerEvents: 'initial' });
        });

    return disposePreloader;
};
